import React from 'react';
import { PlannedStint, StrategyConfig, StrategyDriver } from '../types';
import { ClosedPitWindow } from '../analytics/regulations';
import { LiveStint } from '../analytics/liveStrategy';
import {
  clockAt,
  formatMinutesSeconds,
  formatOffset,
  Violation,
} from '../analytics/stintPlan';
import './StintGrid.css';

interface StintGridProps {
  stints: PlannedStint[];
  roster: StrategyDriver[];
  config: StrategyConfig;
  violations: Violation[];
  minPitMinutes: number;
  /** Null when the bulletin declares a single direction — no reversal to mark. */
  directionChange: ClosedPitWindow | null;
  onChange: (index: number, patch: Partial<PlannedStint>) => void;
  readOnly?: boolean;
  /**
   * Live mode: what has happened to each stint. Finished stints are locked and
   * show what really happened beside what was planned, the one in the kart is
   * highlighted, and only the ones still to come can be edited freely.
   */
  live?: LiveStint[];
}

/** "+3′" / "−2′"; nothing at all when the difference is below `floor`. */
function formatDelta(delta: number | null | undefined, floor: number): string | null {
  if (delta === null || delta === undefined || Math.abs(delta) < floor) return null;
  const sign = delta > 0 ? '+' : '−';
  return `${sign}${Math.round(Math.abs(delta) * 10) / 10}′`;
}

const STATUS_TAG: Partial<Record<LiveStint['status'], string>> = {
  running: 'now',
  boxed: 'in pit',
};

/** How many minutes of a stop count against the plan, for the running total. */
function cumulative(stints: PlannedStint[], index: number): number {
  return stints.slice(0, index + 1).reduce((sum, s) => sum + s.minutes, 0);
}

/**
 * The organiser's Strategy sheet, made editable.
 *
 * Columns match the paper form on pages 11–12 of the regulations exactly —
 * Stint, Start, End, Driver, Driving Time, Number of Pit Stops, Total Driving
 * Time — so a plan can be checked against a steward's copy line by line.
 */
export const StintGrid: React.FC<StintGridProps> = ({
  stints,
  roster,
  config,
  violations,
  minPitMinutes,
  directionChange,
  onChange,
  readOnly = false,
  live,
}) => {
  const byStint = React.useMemo(() => {
    const map = new Map<number, Violation[]>();
    violations.forEach((v) => {
      if (v.stintIndex === null) return;
      const list = map.get(v.stintIndex) ?? [];
      list.push(v);
      map.set(v.stintIndex, list);
    });
    return map;
  }, [violations]);

  const midpoint = directionChange
    ? (directionChange.fromMinute + directionChange.toMinute) / 2
    : 0;

  /**
   * True for the stint the reversal falls inside — the marker sits under the row
   * it interrupts, so it reads in running order rather than floating between
   * two stints that may be an hour apart.
   */
  const reversalAfter = (i: number): boolean => {
    if (!directionChange) return false;
    const from = stints[i].startMinute;
    const to =
      i + 1 < stints.length
        ? stints[i + 1].startMinute
        : stints[i].startMinute + stints[i].minutes;
    return midpoint >= from && midpoint < to;
  };

  if (stints.length === 0) {
    return (
      <div className="sg-empty">
        No stints yet. Add your drivers, then <b>Generate plan</b>.
      </div>
    );
  }

  return (
    <div className="sg-scroll">
      <table className="sg-table">
        <thead>
          <tr>
            <th className="sg-num">Stint</th>
            <th className="sg-num">Start</th>
            <th className="sg-num">End</th>
            <th>Driver</th>
            <th className="sg-num">Driving Time</th>
            <th className="sg-num">Pit Stops</th>
            <th className="sg-num">Total Driving Time</th>
            <th className="sg-stop">Change</th>
          </tr>
        </thead>
        <tbody>
          {stints.map((s, i) => {
            const problems = byStint.get(i) ?? [];
            const worst = problems.some((p) => p.severity === 'error')
              ? 'error'
              : problems.length > 0
                ? 'warning'
                : 'ok';
            const end = s.startMinute + s.minutes;

            const status = live?.[i]?.status;
            const info = live?.[i];
            // History cannot be changed, and the person in the kart cannot be swapped
            // out on paper; how long they stay in can still be.
            const finished = status === 'done' || status === 'done-unobserved';
            const lengthLocked = readOnly || finished;
            const driverLocked = lengthLocked || status === 'running' || status === 'boxed';
            const isLast = i === stints.length - 1;
            const delta = formatDelta(info?.deltaMinutes, finished ? 0.1 : 0.5);
            const driverChanged =
              info !== undefined &&
              info.plannedDriverIndex !== null &&
              info.plannedDriverIndex !== s.driverIndex;

            return (
              <React.Fragment key={i}>
                <tr
                  className={`sg-row sg-row--${worst}${status ? ` sg-live--${status}` : ''}`}
                  aria-current={status === 'running' || status === 'boxed' ? 'step' : undefined}
                >
                  <td className="sg-num sg-index">
                    {i + 1}
                    {status && STATUS_TAG[status] && (
                      <span className="sg-live-tag">{STATUS_TAG[status]}</span>
                    )}
                  </td>
                  <td className="sg-num sg-clock">
                    <span className="sg-wall">{clockAt(config.startsAt, s.startMinute)}</span>
                    <span className="sg-offset">{formatOffset(s.startMinute)}</span>
                  </td>
                  <td className="sg-num sg-clock">
                    <span className="sg-wall">{clockAt(config.startsAt, end)}</span>
                    <span className="sg-offset">{formatOffset(end)}</span>
                  </td>
                  <td>
                    <select
                      className="sg-driver"
                      value={s.driverIndex ?? ''}
                      disabled={driverLocked}
                      aria-label={`Driver for stint ${i + 1}`}
                      onChange={(e) =>
                        onChange(i, {
                          driverIndex: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    >
                      <option value="">—</option>
                      {roster.map((d, di) => (
                        <option key={di} value={di}>
                          {d.name}
                          {d.unavailable ? ' (out)' : ''}
                        </option>
                      ))}
                    </select>
                    {driverChanged && info && (
                      <span className="sg-was">
                        planned {roster[info.plannedDriverIndex as number]?.name ?? '—'}
                      </span>
                    )}
                  </td>
                  <td className="sg-num">
                    {lengthLocked ? (
                      <span className="sg-minutes-text" aria-label={`Driving minutes for stint ${i + 1}`}>
                        {Math.round(s.minutes * 100) / 100}
                      </span>
                    ) : (
                      <input
                        className="sg-minutes"
                        type="number"
                        min={0}
                        step={0.5}
                        value={Math.round(s.minutes * 100) / 100}
                        disabled={readOnly}
                        aria-label={`Driving minutes for stint ${i + 1}`}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n) && n >= 0) onChange(i, { minutes: n });
                        }}
                      />
                    )}
                    {!lengthLocked && !isLast && (
                      <button
                        type="button"
                        className={`sg-pin${s.pinned ? ' sg-pin--on' : ''}`}
                        aria-pressed={s.pinned === true}
                        aria-label={`Keep stint ${i + 1} at this length`}
                        title={
                          s.pinned
                            ? 'Pinned: a re-plan will not change this length. Click to release.'
                            : 'Pin this length so a re-plan leaves it alone'
                        }
                        onClick={() => onChange(i, { pinned: !s.pinned })}
                      >
                        📌
                      </button>
                    )}
                    {info && finished && info.plannedMinutes !== s.minutes && (
                      <span className="sg-was">
                        planned <s>{Math.round(info.plannedMinutes * 100) / 100}</s>
                      </span>
                    )}
                    {delta && (
                      <span className={`sg-delta ${info && info.deltaMinutes! > 0 ? 'sg-delta--long' : 'sg-delta--short'}`}>
                        {delta}
                      </span>
                    )}
                  </td>
                  <td className="sg-num sg-muted">{i < stints.length - 1 ? i + 1 : i}</td>
                  <td className="sg-num sg-muted">{formatOffset(cumulative(stints, i))}</td>
                  <td className="sg-stop">
                    {s.stopKind === 'none' ? (
                      <span className="sg-flag">to the flag</span>
                    ) : (
                      <span className={`sg-kind sg-kind--${s.stopKind}`}>
                        {s.stopKind === 'kart-only' ? 'kart only' : 'driver + kart'}
                      </span>
                    )}
                    {i < stints.length - 1 && (
                      <span className="sg-pit">
                        +{formatMinutesSeconds(minPitMinutes)} pit
                      </span>
                    )}
                  </td>
                </tr>
                {problems.map((p, pi) => (
                  <tr key={`${i}-v${pi}`} className={`sg-issue sg-issue--${p.severity}`}>
                    <td />
                    <td colSpan={7}>
                      <span className="sg-rule">Rule {p.rule}</span>
                      {p.message}
                      {p.penalty && <span className="sg-penalty">{p.penalty}</span>}
                    </td>
                  </tr>
                ))}
                {reversalAfter(i) && directionChange && (
                  <tr className="sg-reverse">
                    <td />
                    <td colSpan={7}>
                      <span className="sg-reverse-mark">⇄ Direction change</span>
                      <span className="sg-reverse-at">
                        {clockAt(config.startsAt, midpoint)} ·{' '}
                        {formatOffset(midpoint)}
                      </span>
                      <span className="sg-reverse-note">
                        Pit lane closed {formatOffset(directionChange.fromMinute)}–
                        {formatOffset(directionChange.toMinute)}, and for the same
                        again after the race resumes
                      </span>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default StintGrid;
